<template>
  <div class="buoyancy-viz-3d">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>F<sub>浮</sub> = ρ<sub>液</sub> · g · V<sub>排</sub></div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="simulation-stage">
        <div class="hud-panel">
          <div class="hud-item blue">
            <label>浸入高度</label>
            <span class="val">{{ depth.toFixed(1) }} <small>cm</small></span>
          </div>
          <div class="hud-item red">
            <label>实时浮力</label>
            <span class="val">{{ buoyancyForce.toFixed(2) }} <small>N</small></span>
          </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
           <div class="control-item">
              <div class="label-row">
                 <span class="label">垂直位置 (H)</span>
                 <span class="val">{{ (100 - yPos).toFixed(0) }} cm</span>
              </div>
              <el-slider v-model="yPos" :min="10" :max="100" size="small"></el-slider>
           </div>
           <div class="control-item">
              <div class="label-row">
                 <span class="label">液体密度 (ρ)</span>
                 <span class="val">{{ rho }} kg/m³</span>
              </div>
              <el-slider v-model="rho" :min="800" :max="1500" :step="50" size="small"></el-slider>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>物体底部受到的向上压力大于顶部受到的向下压力，这种<strong>压力差</strong>构成了浮力。排开的体积越大，压力差越显著。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'BuoyancyViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      yPos: 80,
      rho: 1000,
      running: true
    }
  },
  computed: {
    depth() {
      const waterLevel = 50;
      return Math.max(0, Math.min(30, waterLevel - this.yPos + 30)); 
    },
    buoyancyForce() {
      return (this.rho * 9.8 * (this.depth * 25) / 1000000) * 10;
    }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf1f5f9);

      this.camera = new THREE.PerspectiveCamera(40, container.clientWidth / container.clientHeight, 1, 1000);
      this.camera.position.set(50, 45, 85);
      this.camera.lookAt(0, 20, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(this.renderer.domElement);

      const tankGeo = new THREE.BoxGeometry(45, 60, 45);
      const tankMat = new THREE.MeshPhongMaterial({ color: 0x2563eb, transparent: true, opacity: 0.1, side: THREE.BackSide });
      const tank = new THREE.Mesh(tankGeo, tankMat);
      tank.position.y = 30;
      this.scene.add(tank);

      const waterGeo = new THREE.BoxGeometry(45, 50, 45);
      const waterMat = new THREE.MeshPhongMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.35 });
      this.scene.add(new THREE.Mesh(waterGeo, waterMat)).position.y = 25;

      const cubeGeo = new THREE.BoxGeometry(15, 30, 15);
      const cubeMat = new THREE.MeshPhongMaterial({ color: 0xf59e0b, shininess: 30 });
      this.cube = new THREE.Mesh(cubeGeo, cubeMat);
      this.scene.add(this.cube);

      this.upArrow = new THREE.ArrowHelper(new THREE.Vector3(0,1,0), new THREE.Vector3(0,0,0), 1, 0xef4444, 5, 2);
      this.downArrow = new THREE.ArrowHelper(new THREE.Vector3(0,-1,0), new THREE.Vector3(0,0,0), 1, 0xef4444, 5, 2);
      this.scene.add(this.upArrow);
      this.scene.add(this.downArrow);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));
      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(30, 100, 50);
      this.scene.add(light);

      this.animate();
    },
    animate() {
      if (!this.running) return;
      this.frameId = requestAnimationFrame(this.animate);
      const h = (this.yPos - 50) + 15;
      this.cube.position.y = h;
      const dBottom = Math.max(0, 50 - (h - 15));
      const dTop = Math.max(0, 50 - (h + 15));
      this.upArrow.position.set(0, h-15, 0);
      this.upArrow.setLength(Math.max(0.1, dBottom * 1.5), 5, 2);
      this.upArrow.visible = dBottom > 0;
      this.downArrow.position.set(0, h+15, 0);
      this.downArrow.setLength(Math.max(0.1, dTop * 1.5), 5, 2);
      this.downArrow.visible = dTop > 0;
      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.buoyancy-viz-3d { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #64748b; font-weight: normal; } } &.blue { border-left: 3px solid #3b82f6; } &.red { border-left: 3px solid #ef4444; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .control-item { .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
